import axios from 'axios';

const getApiUrl = () => {
  // If explicitly provided via env AND is not localhost
  if (process.env.REACT_APP_API_URL && !process.env.REACT_APP_API_URL.includes('localhost')) {
    return process.env.REACT_APP_API_URL;
  }
  // In browser environment
  if (typeof window !== 'undefined' && window.location) {
    // ONLY use localhost port 5010 if running the React dev server locally on port 3000 or 3010
    if (
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') &&
      (window.location.port === '3000' || window.location.port === '3010')
    ) {
      return `http://${window.location.hostname}:5010/api`;
    }
    // In ALL other environments (production domain pxkaizen.mailops.online, server IP, etc.)
    // always use relative URL '/api' so it routes to the same host
    return '/api';
  }
  return '/api';
};

export const API_URL = getApiUrl();

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use(config => {
  const token = localStorage.getItem('accessToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

let isRefreshing = false, failedQueue = [];
const processQueue = (error, token = null) => {
  failedQueue.forEach(p => error ? p.reject(error) : p.resolve(token));
  failedQueue = [];
};

api.interceptors.response.use(
  res => res,
  async error => {
    const original = error.config;
    if (error.response?.status === 401 && error.response?.data?.code === 'TOKEN_EXPIRED' && !original._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => { failedQueue.push({ resolve, reject }); })
          .then(token => { original.headers.Authorization = `Bearer ${token}`; return api(original); });
      }
      original._retry = true; isRefreshing = true;
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) { isRefreshing = false; window.location.href = '/login'; return Promise.reject(error); }
      try {
        const res = await axios.post(`${API_URL}/auth/refresh`, { refreshToken });
        const { accessToken, refreshToken: newRT } = res.data;
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', newRT);
        processQueue(null, accessToken);
        original.headers.Authorization = `Bearer ${accessToken}`;
        return api(original);
      } catch (err) {
        processQueue(err, null); localStorage.clear(); window.location.href = '/login'; return Promise.reject(err);
      } finally { isRefreshing = false; }
    }
    return Promise.reject(error);
  }
);

export const authApi = {
  login:   data => api.post('/auth/login', data),
  logout:  rt   => api.post('/auth/logout', { refreshToken: rt }),
  me:      ()   => api.get('/auth/me'),
};

export const reportsApi = {
  list:         params        => api.get('/reports', { params }),
  categories:   ()            => api.get('/reports/categories'),
  getData:      (id, refresh) => api.get(`/reports/${id}/data`, { params: refresh ? { refresh: 'true' } : {} }),
  // Returns a proxied URL the browser can use to render PDF inline.
  // Auth token is passed as a query param so the <object> tag can load it.
  pdfStreamUrl: (id)          => {
    const token = localStorage.getItem('accessToken');
    return `${API_URL}/reports/${id}/pdf-stream?token=${encodeURIComponent(token)}`;
  },
  mapFolder:    (id, data)    => api.patch(`/reports/${id}/folder`, data),
  unmapFolder:  (id)          => api.delete(`/reports/${id}/folder`),
};

export const usersApi = {
  list:                 params     => api.get('/users', { params }),
  create:               data       => api.post('/users', data),
  update:               (id, data) => api.put(`/users/${id}`, data),
  delete:               id         => api.delete(`/users/${id}`),
  getReports:           id         => api.get(`/users/${id}/reports`),
  unprovisionedClients: ()         => api.get('/users/unprovisioned-clients'),
  provisionClient:      data       => api.post('/users/provision-client', data),
};

export const accessApi = {
  matrix: ()     => api.get('/access/matrix'),
  grant:  data   => api.post('/access/grant', data),
  revoke: data   => api.post('/access/revoke', data),
  bulk:   data   => api.post('/access/bulk', data),
  toggleCustom: data => api.post('/access/toggle-custom', data),
};

export const adminApi = {
  stats:          ()     => api.get('/admin/stats'),
  auditLogs:      params => api.get('/admin/audit-logs', { params }),
  syncStatus:     ()     => api.get('/sync/status'),
  triggerSync:    ()     => api.post('/sync/trigger'),
  testOneDrive:   ()     => api.get('/admin/onedrive-test'),
  listOneDrive:   params => api.get('/onedrive/list', { params }),
  searchOneDrive: params => api.get('/onedrive/search', { params }),
  runDistribution: data  => api.post('/admin/distribution/run', data),
  getFileData:    id     => api.get(`/onedrive/file/${id}/data`),
  pdfFileStreamUrl: id   => {
    const token = localStorage.getItem('accessToken');
    return `${API_URL}/onedrive/file/${id}/pdf-stream?token=${encodeURIComponent(token)}`;
  },
  getFileRemarks: id     => api.get(`/onedrive/file/${id}/remarks`),
  addFileRemark: (id, data) => api.post(`/onedrive/file/${id}/remarks`, data),
  operationsSummary: (refresh = false) => api.get('/onedrive/operations-summary', { params: refresh ? { refresh: 'true' } : {} }),
  centralReports: (refresh = false) => api.get('/onedrive/central-reports', { params: refresh ? { refresh: 'true' } : {} }),
  centralReportsExportUrl: `${API_URL}/onedrive/central-reports/export`,
  triggerArchiveSync: (date = null) => api.post('/onedrive/central-reports/archive-sync', { date }),
  getArchiveStatus: () => api.get('/onedrive/central-reports/archive-status'),
  getArchiveHistory: (date) => api.get(`/onedrive/central-reports/archive-history/${encodeURIComponent(date)}`),
  updateFileRowVisibility: (id, data) => api.post(`/onedrive/file/${id}/visibility`, data),
};

export const emailFlowApi = {
  list:         params        => api.get('/email-flows', { params }),
  get:          id            => api.get(`/email-flows/${id}`),
  byEmail:      email         => api.get(`/email-flows/by-email/${encodeURIComponent(email)}`),
  getData:      (id, refresh) => api.get(`/email-flows/${id}/data`, { params: refresh ? { refresh: 'true' } : {} }),
  pdfStreamUrl: (flowId, itemId) => {
    const token = localStorage.getItem('accessToken');
    return `${API_URL}/email-flows/${flowId}/pdf-stream?token=${encodeURIComponent(token)}&itemId=${encodeURIComponent(itemId)}`;
  },
  create:       data          => api.post('/email-flows', data),
  update:       (id, data)    => api.put(`/email-flows/${id}`, data),
  delete:       id            => api.delete(`/email-flows/${id}`),
  importBulk:   flows         => api.post('/email-flows/import/bulk', { flows }),
};

export const governanceApi = {
  stats: () => api.get('/governance/stats'),
  folders: () => api.get('/governance/folders'),
};

export const aiApi = {
  getEngineStatus: () => api.get('/ai/engine-status'),
  chat: (data) => api.post('/ai/chat', data),
  getConversations: () => api.get('/ai/conversations'),
  getMessages: (id) => api.get(`/ai/conversations/${id}`),
  getProactiveBriefing: () => api.get('/ai/proactive-briefing'),
};

export default api;
